import { request } from '../../api';

export interface LprSiteConfig {
  readonly siteId: string;
  readonly vendor: string;
  readonly endpoint: string | null;
  readonly confidenceThreshold: number;
  readonly dedupWindowMs: number;
  readonly vendorSilenceMs: number;
  readonly status: 'active' | 'paused';
  readonly latitude?: number | null;
  readonly longitude?: number | null;
  readonly minRecognitionRate?: number;
  readonly exitMatchWindowMs?: number;
}

export interface LprCamera {
  readonly id: string;
  readonly siteId: string;
  readonly code: string;
  readonly label: string;
  readonly direction: 'entry' | 'exit';
  readonly vendorCameraId: string | null;
  readonly status: 'active' | 'disabled';
}

export interface CamerasResponse {
  readonly site: LprSiteConfig | null;
  readonly cameras: readonly LprCamera[];
  readonly vendors: readonly string[];
}

export interface StageLatency {
  readonly stage: 'vendor' | 'resolve' | 'deliver' | 'render' | 'total';
  readonly samples: number;
  readonly p50Ms: number | null;
  readonly p95Ms: number | null;
}

export interface QualityReport {
  readonly siteId: string;
  readonly totals: {
    readonly total: number;
    readonly identified: number;
    readonly unidentified: number;
    readonly lowConfidence: number;
    readonly duplicate: number;
    readonly noShift: number;
    readonly failed: number;
    readonly manual: number;
  };
  readonly rates: {
    readonly recognized: number;
    readonly lowConfidence: number;
    readonly manual: number;
    readonly failed: number;
  };
  readonly latency: readonly StageLatency[];
  readonly unacked: number;
  readonly byCamera: ReadonlyArray<{
    readonly cameraId: string | null;
    readonly cameraCode: string | null;
    readonly total: number;
    readonly identified: number;
    readonly lowConfidence: number;
    readonly failed: number;
  }>;
  readonly byHour: ReadonlyArray<{
    readonly hour: number;
    readonly total: number;
    readonly identified: number;
    readonly lowConfidence: number;
    readonly failed: number;
  }>;
  readonly byWeather: ReadonlyArray<{
    readonly weather: string;
    readonly total: number;
    readonly identified: number;
    readonly lowConfidence: number;
    readonly failed: number;
  }>;
  /** Приёмка по условиям съёмки: ночь, осадки, сухой день (М2.3). */
  readonly conditions?: ReadonlyArray<{
    readonly condition: 'night' | 'precipitation' | 'day_dry';
    readonly attempts: number;
    readonly recognized: number;
    readonly rate: number;
    readonly threshold: number;
    readonly pass: boolean | null;
  }>;
  /** Время цикла по выездам, отмеченным камерой (М2.7). */
  readonly cycle?: {
    readonly samples: number;
    readonly avgSeconds: number | null;
    readonly p50Seconds: number | null;
    readonly p95Seconds: number | null;
  };
  readonly licence: ReadonlyArray<{
    readonly vendor: string;
    readonly calls: number;
    readonly units: number;
    readonly failures: number;
  }>;
}

export interface VendorHealthResponse {
  readonly vendor: string;
  readonly endpoint: string | null;
  readonly state: {
    readonly available: boolean;
    readonly since: string;
    readonly lastSeen: string | null;
    readonly lastError: string | null;
  };
  readonly listeners: number;
}

export interface SaveConfigResponse {
  readonly site: LprSiteConfig;
  readonly secret: string | null;
}

export function fetchCameras(siteId: string): Promise<CamerasResponse> {
  return request<CamerasResponse>('GET', `/api/v1/lpr/cameras?site_id=${encodeURIComponent(siteId)}`);
}

export function fetchQuality(siteId: string, from: Date): Promise<QualityReport> {
  const query = `site_id=${encodeURIComponent(siteId)}&from=${encodeURIComponent(from.toISOString())}`;
  return request<QualityReport>('GET', `/api/v1/lpr/quality?${query}`);
}

export function fetchHealth(siteId: string): Promise<VendorHealthResponse> {
  return request<VendorHealthResponse>('GET', `/api/v1/lpr/health?site_id=${encodeURIComponent(siteId)}`);
}

export function saveCamera(body: {
  readonly siteId: string;
  readonly code: string;
  readonly label?: string;
  readonly direction?: 'entry' | 'exit';
  readonly vendorCameraId?: string | null;
  readonly status?: 'active' | 'disabled';
}): Promise<LprCamera> {
  return request<LprCamera>('POST', '/api/v1/lpr/cameras', body);
}

export function saveSiteConfig(body: {
  readonly siteId: string;
  readonly vendor?: string;
  readonly endpoint?: string | null;
  readonly confidenceThreshold?: number;
  readonly dedupWindowMs?: number;
  readonly vendorSilenceMs?: number;
  readonly rotateSecret?: boolean;
  readonly latitude?: number | null;
  readonly longitude?: number | null;
  readonly minRecognitionRate?: number;
}): Promise<SaveConfigResponse> {
  return request<SaveConfigResponse>('POST', '/api/v1/lpr/cameras/site', body);
}
